import { ref, type Ref } from 'vue'
import QRCode from 'qrcode'
import { QrcodeStream } from 'vue-qrcode-reader'

export function useQRCode() {
  const scannerActive: Ref<boolean> = ref(false)
  const cameraPermission: Ref<boolean | null> = ref(null)
  const lastScannedCode: Ref<string> = ref('')
  const availableCameras: Ref<MediaDeviceInfo[]> = ref([])
  const selectedCameraId: Ref<string | undefined> = ref(undefined)

  // Obtener lista de cámaras disponibles
  const getCameras = async (): Promise<MediaDeviceInfo[]> => {
    let tempStream: MediaStream | null = null
    try {
      // Primero solicitar permiso para acceder a las cámaras
      tempStream = await navigator.mediaDevices.getUserMedia({ video: true })
      const devices = await navigator.mediaDevices.enumerateDevices()
      const cameras = devices.filter(device => device.kind === 'videoinput')
      availableCameras.value = cameras
      
      console.log('[QR Scanner] Cámaras encontradas:', cameras.map(c => ({
        id: c.deviceId,
        label: c.label || `Cámara ${c.deviceId.slice(0, 5)}...`
      })))
      
      return cameras
    } catch (err) {
      console.error('[QR Scanner] Error obteniendo cámaras:', err)
      return []
    } finally {
      // IMPORTANTE: Liberar el stream temporal para no bloquear la cámara
      if (tempStream) {
        tempStream.getTracks().forEach(track => track.stop())
        console.log('[QR Scanner] 🧹 Stream temporal liberado')
      }
    }
  }

  // Seleccionar cámara que no sea virtual
  const selectBestCamera = async () => {
    const cameras = await getCameras()
    
    // Primero intentar usar preferencia guardada
    const savedId = loadCameraId()
    if (savedId) {
      const savedCamera = cameras.find(c => c.deviceId === savedId)
      if (savedCamera) {
        console.log('[QR Scanner] 🔄 Usando cámara guardada:', savedCamera.label)
        selectedCameraId.value = savedCamera.deviceId
        return
      } else {
        console.log('[QR Scanner] ⚠️ Cámara guardada no encontrada, seleccionando automáticamente')
      }
    }
    
    // Palabras clave que identifican cámaras virtuales
    const virtualKeywords = [
      'virtual', 'iriun', 'obs', 'snap', 'manycam', 
      'droidcam', 'ip camera', 'android', 'ios', 'iphone'
    ]
    
    // Buscar cámaras reales (excluyendo virtuales)
    const realCamera = cameras.find(camera => {
      const label = camera.label.toLowerCase()
      const isVirtual = virtualKeywords.some(keyword => label.includes(keyword))
      return !isVirtual // Si no es virtual, es real
    })
    
    if (realCamera) {
      console.log('[QR Scanner] ✅ Cámara real seleccionada:', realCamera.label)
      selectedCameraId.value = realCamera.deviceId
    } else {
      console.log('[QR Scanner] ⚠️ No se encontró cámara real, usando la primera disponible')
      console.log('[QR Scanner] Cámaras disponibles:', cameras.map(c => c.label).join(', '))
      selectedCameraId.value = cameras[0]?.deviceId
    }
  }

  // Guardar preferencia de cámara
  const saveCameraId = (deviceId: string) => {
    console.log('[QR Scanner] 💾 Guardando preferencia de cámara:', deviceId)
    localStorage.setItem('preferredCamera', deviceId)
    selectedCameraId.value = deviceId
  }

  // Cargar preferencia de cámara
  const loadCameraId = (): string | null => {
    const saved = localStorage.getItem('preferredCamera')
    if (saved) {
      console.log('[QR Scanner] 📂 Cámara guardada encontrada:', saved)
    }
    return saved
  }

  const generateQR = async (text: string, containerId: string): Promise<void> => {
    const qrContainer = document.getElementById(containerId)
    if (!qrContainer) return

    qrContainer.innerHTML = ''

    try {
      const canvas = await QRCode.toCanvas(text, {
        width: 350,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'L',
        version: 40
      })
      qrContainer.appendChild(canvas)
    } catch (err) {
      console.error('[QR Generate] Error generating QR:', err)
    }
  }

  const onDetect = (detectedCodes: any[]): string | null => {
    console.log('[QR Scanner] Códigos detectados:', detectedCodes.length)
    
    if (detectedCodes.length > 0) {
      const code = detectedCodes[0].rawValue
      console.log('[QR Scanner] ✅ QR ESCANEADO:', code)
      console.log('[QR Scanner] Longitud:', code.length)
      console.log('[QR Scanner] Primeros 50 caracteres:', code.substring(0, 50))
      
      // Validar que el código no esté vacío
      if (!code || code.trim().length === 0) {
        console.warn('[QR Scanner] ⚠️ Código vacío, ignorando...')
        return null
      }

      // Evitar escaneos duplicados consecutivos
      if (code === lastScannedCode.value) {
        console.log('[QR Scanner] Código duplicado, ignorando...')
        return null
      }

      lastScannedCode.value = code
      return code
    }

    return null
  }

  const onCameraReady = (capabilities: any) => {
    console.log('[QR Scanner] 📷 Cámara lista')
    console.log('[QR Scanner] Capabilities:', capabilities)
    cameraPermission.value = true
    scannerActive.value = true

    // Check if torch is supported
    if (capabilities.torch) {
      console.log('[QR Scanner] ✅ Torch/Flash disponible')
    }
  }

  const onError = (error: any) => {
    console.error('[QR Scanner] ❌ Error:', error)
    scannerActive.value = false

    switch (error.name) {
      case 'NotAllowedError':
        cameraPermission.value = false
        alert('Permiso de cámara denegado. Por favor, permite el acceso a la cámara en tu navegador.')
        break
      case 'NotFoundError':
        cameraPermission.value = false
        alert('No se encontró ninguna cámara en el dispositivo.')
        break
      case 'NotSupportedError':
        cameraPermission.value = false
        alert('Tu navegador no soporta acceso a la cámara o necesitas usar HTTPS.')
        break
      case 'NotReadableError':
        cameraPermission.value = false
        alert('La cámara ya está siendo usada por otra aplicación.')
        break
      case 'OverconstrainedError':
        cameraPermission.value = false
        alert('La cámara solicitada no está disponible en este dispositivo.')
        break
      default:
        cameraPermission.value = false
        alert('Error iniciando el escáner: ' + (error.message || 'Error desconocido'))
    }
  }

  const resetScannerState = () => {
    lastScannedCode.value = ''
    scannerActive.value = false
  }

  return {
    // Componentes
    QrcodeStream,
    
    // Estado
    scannerActive,
    cameraPermission,
    lastScannedCode,
    availableCameras,
    selectedCameraId,
    
    // Funciones de generación
    generateQR,
    
    // Funciones de cámara
    getCameras,
    selectBestCamera,
    saveCameraId,
    loadCameraId,
    
    // Event handlers del scanner
    onDetect,
    onCameraReady,
    onError,
    
    // Utilidades
    resetScannerState
  }
}